import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft'
import { ArrowsInSimple as ArrowsInSimpleIcon } from '@phosphor-icons/react/dist/ssr/ArrowsInSimple'
import { ArrowsOutSimple as ArrowsOutSimpleIcon } from '@phosphor-icons/react/dist/ssr/ArrowsOutSimple'
import { DotsThree as DotsThreeIcon } from '@phosphor-icons/react/dist/ssr/DotsThree'
import { Minus as MinusIcon } from '@phosphor-icons/react/dist/ssr/Minus'
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'

import { AssistantEmblem } from './assistant-emblem'

export type ChatPanelView = 'history' | 'draft' | 'chat'

export interface ChatPanelHeaderProps {
	view: ChatPanelView
	isExpanded: boolean
	isMobile: boolean
	menuOpen: boolean
	onBackToHistory: () => void
	onCancelDraft: () => void
	onMinimize: () => void
	onClose: () => void
	onNewConversation: () => void
	onOpenMenu: (anchorEl: HTMLElement) => void
	onToggleExpanded: () => void
}

export function ChatPanelHeader({
	view,
	isExpanded,
	isMobile,
	menuOpen,
	onBackToHistory,
	onCancelDraft,
	onMinimize,
	onClose,
	onNewConversation,
	onOpenMenu,
	onToggleExpanded,
}: ChatPanelHeaderProps) {
	const iconSize = isMobile ? 20 : 16
	const iconButtonSx = {
		color: 'text.primary',
		width: { xs: 44, sm: 32 },
		height: { xs: 44, sm: 32 },
	} as const

	return (
		<Stack
			direction="row"
			spacing={1}
			sx={{
				alignItems: 'center',
				px: 1,
				py: 1,
				pt: { xs: 'calc(8px + env(safe-area-inset-top))', sm: 1 },
				minHeight: 48,
				position: 'relative',
				// Soft fade below the header instead of a hard divider.
				'&::after': {
					background: 'linear-gradient(to bottom, var(--mui-palette-background-default), transparent)',
					bottom: -10,
					content: '""',
					height: 10,
					left: 0,
					pointerEvents: 'none',
					position: 'absolute',
					right: 0,
					zIndex: 1,
				},
			}}
		>
			{view === 'history' ? (
				<>
					<Typography fontWeight={600} sx={{ pl: 1.5 }} variant="subtitle2">
						Chat history
					</Typography>
					<Tooltip title="New conversation">
						<IconButton
							aria-label="New conversation"
							onClick={(event) => {
								event.currentTarget.blur()
								onNewConversation()
							}}
							size="small"
							sx={iconButtonSx}
						>
							<PlusIcon size={iconSize} weight="bold" />
						</IconButton>
					</Tooltip>
					<Box sx={{ flex: 1 }} />
				</>
			) : view === 'draft' ? (
				<>
					<IconButton aria-label="Back to chat history" onClick={onCancelDraft} size="small" sx={iconButtonSx}>
						<ArrowLeftIcon size={iconSize} weight="bold" />
					</IconButton>
					<Typography fontWeight={600} sx={{ flex: 1 }} variant="subtitle2">
						New conversation
					</Typography>
				</>
			) : (
				<>
					<IconButton aria-label="Back to conversations" onClick={onBackToHistory} size="small" sx={iconButtonSx}>
						<ArrowLeftIcon size={iconSize} weight="bold" />
					</IconButton>
					<Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
						<AssistantEmblem size={28} />
						<Typography fontWeight={600} variant="subtitle2">
							Assistant
						</Typography>
						<Tooltip title="Conversation options">
							<IconButton
								aria-expanded={menuOpen}
								aria-haspopup="menu"
								aria-label="Conversation options"
								onClick={(event) => {
									event.currentTarget.blur()
									onOpenMenu(event.currentTarget)
								}}
								size="small"
								sx={iconButtonSx}
							>
								<DotsThreeIcon size={iconSize + 4} weight="bold" />
							</IconButton>
						</Tooltip>
					</Stack>
				</>
			)}
			{/* Minimize keeps the conversation for next open; close starts a new one. */}
			<Stack direction="row" spacing={0}>
				<Tooltip title="Minimize">
					<IconButton
						aria-label="Minimize assistant"
						onClick={(event) => {
							event.currentTarget.blur()
							onMinimize()
						}}
						size="small"
						sx={iconButtonSx}
					>
						<Box component="span" sx={{ display: 'inline-flex', transform: 'translateY(5px)' }}>
							<MinusIcon size={iconSize} weight="bold" />
						</Box>
					</IconButton>
				</Tooltip>
				<Tooltip title={isExpanded ? 'Collapse window' : 'Expand window'}>
					<IconButton
						aria-label={isExpanded ? 'Collapse assistant window' : 'Expand assistant window'}
						onClick={(event) => {
							event.currentTarget.blur()
							onToggleExpanded()
						}}
						size="small"
						sx={{ ...iconButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}
					>
						{isExpanded ? (
							<ArrowsInSimpleIcon size={iconSize} weight="bold" />
						) : (
							<ArrowsOutSimpleIcon size={iconSize} weight="bold" />
						)}
					</IconButton>
				</Tooltip>
				<Tooltip title="Close assistant">
					<IconButton
						aria-label="Close assistant"
						onClick={(event) => {
							event.currentTarget.blur()
							onClose()
						}}
						size="small"
						sx={iconButtonSx}
					>
						<XIcon size={iconSize} weight="bold" />
					</IconButton>
				</Tooltip>
			</Stack>
		</Stack>
	)
}
